"use client";

import { Turnstile } from "@marsidev/react-turnstile";

// Inlined at build time by Next. When unset, the widget renders nothing and
// callers skip the token requirement — Turnstile is fully env-gated, so signup
// keeps working until the site key + Supabase Auth secret are provisioned.
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/** Whether a Cloudflare Turnstile challenge should be required on auth forms. */
export function isTurnstileEnabled(): boolean {
  return Boolean(SITE_KEY);
}

interface TurnstileWidgetProps {
  /** Receives the solved token, or null on error/expiry/reset. */
  onToken: (token: string | null) => void;
  className?: string;
}

/**
 * Cloudflare Turnstile widget. Render anywhere on an auth form. Remount it (via a
 * changing React `key`) to force a fresh challenge after a failed submit, since
 * Turnstile tokens are single-use.
 */
export function TurnstileWidget({ onToken, className }: TurnstileWidgetProps) {
  if (!SITE_KEY) return null;
  return (
    <div className={className}>
      <Turnstile
        siteKey={SITE_KEY}
        onSuccess={(token) => onToken(token)}
        onError={() => onToken(null)}
        onExpire={() => onToken(null)}
        // interaction-only: the widget stays invisible and auto-issues a token
        // for normal visitors, only surfacing a challenge if Cloudflare flags the
        // session as suspicious. Low friction — most users never see it.
        options={{ theme: "auto", size: "flexible", appearance: "interaction-only" }}
      />
    </div>
  );
}
